// ReviewItemGroupDrawer — decides a grouped review entry (reviewItemGroups)
// in one action. Every member is still its own review item: the drawer sends
// each one its own decision, the same decision its single drawer would send.
//
//   - teams: each stated team with the row it was read from, its name
//     editable, and "Not a team" to leave that one blank. One button confirms
//     them all.
//   - same: the one conflict the lines share, its choices, and one button
//     that sets the picked value on every open line (or leaves them blank).
//   - silent: the award deliverables a SOW never mentions (SAD-969), each
//     owed per the award schedule unless marked an exception; a row the
//     SOW's PWS scope list pre-filled says so. One button answers them all.
//   - row: the questions one SOW row raises (SAD-972), the row quoted once,
//     each question its own choice; one button answers the ones picked.
//   - repeat: the question several SOWs raise on the same text (M7), asked
//     once with the choices that mean the same in every SOW; one button
//     answers each SOW's item with its own candidate. A SOW that needs a
//     team-relative answer (routed elsewhere) opens on its own.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraFormField, SpecraInput, SpecraOverlayDrawer, SpecraSectionHeader, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useIsViewOnly } from '../../../../../contexts/authContext';
import { useResolveDiscrepancyGroup } from '../../../PreAward/awardCompareHooks';
import { teamCorrectedValueJson } from '../../lib/correctedValue';
import { displayValue, enteredRationale, firstPage, isOpenReviewItem, leftBlankRationale, NO_TARGET_NAMES, pickRationale, recordLabel, resolutionLabel, scheduleRuleWords, targetName } from './reviewItemCopy';
import { groupDocuments, groupTitle, memberLine, memberNoun, rowPassage, sharedChoices, sowDocumentOf, statedTeamName } from './reviewItemGroups';
import { isScheduleRuleConflict, ScheduleRuleCards } from './ScheduleRuleCards';
const NOT_A_TEAM = 'Not a team: the billing table names it, but it is not one of the program’s teams';
export function ReviewItemGroupDrawer({ group, open, onClose, changeSetId, names = NO_TARGET_NAMES, onOpenItem }) {
    const onOpenChange = (next)=>{
        if (!next) onClose();
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open && !!group,
        onOpenChange: onOpenChange,
        eyebrow: group ? group.mode === 'teams' ? 'Teams' : group.mode === 'repeat' ? `${groupDocuments(group).length} ${memberNoun(group, groupDocuments(group).length)}` : `${group.members.length} ${memberNoun(group)}` : undefined,
        title: group ? groupTitle(group, names) : '',
        testId: Testids.reviewItem.groupDrawer,
        width: "lg",
        children: group && (group.mode === 'teams' ? /*#__PURE__*/ _jsx(TeamGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose
        }, group.id) : group.mode === 'silent' ? /*#__PURE__*/ _jsx(SilentGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose,
            names: names
        }, group.id) : group.mode === 'row' ? /*#__PURE__*/ _jsx(RowGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose
        }, group.id) : group.mode === 'repeat' ? /*#__PURE__*/ _jsx(RepeatGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose,
            onOpenItem: onOpenItem
        }, group.id) : /*#__PURE__*/ _jsx(SameValueGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose,
            names: names
        }, group.id))
    });
}
function source(r) {
    return {
        quote: r.sources[0]?.quote ?? '',
        page: firstPage(r.sources)
    };
}
function TeamGroup({ group, changeSetId, onDone }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const [names, setNames] = useState(()=>Object.fromEntries(group.members.map((m)=>[
                m.id,
                statedTeamName(m.review)
            ])));
    const [rejected, setRejected] = useState({});
    const openMembers = group.members.filter(isOpenReviewItem);
    const decisions = useMemo(()=>{
        return openMembers.map((m)=>{
            if (rejected[m.id]) {
                return {
                    discrepancyId: m.id,
                    resolutionState: 'Waived',
                    decisionRationale: leftBlankRationale(NOT_A_TEAM, m.review)
                };
            }
            const stated = m.review.candidates.find((c)=>!c.workstreamId) ?? m.review.candidates[0];
            const name = (names[m.id] ?? '').trim();
            const existing = m.review.candidates.find((c)=>c.workstreamId && c.label === name);
            const edited = !stated || name !== stated.label;
            return {
                discrepancyId: m.id,
                resolutionState: 'Corrected',
                decisionRationale: edited || !stated ? enteredRationale(name, '') : pickRationale(stated, ''),
                decidedValueJson: existing?.workstreamId ? teamCorrectedValueJson({
                    workstreamId: existing.workstreamId
                }) : teamCorrectedValueJson({
                    newTeamName: name
                })
            };
        });
    }, [
        openMembers,
        names,
        rejected
    ]);
    const confirming = decisions.filter((d)=>d.resolutionState === 'Corrected').length;
    const blankName = decisions.some((d)=>d.resolutionState === 'Corrected' && !d.decidedValueJson);
    const onConfirm = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Confirmed ${confirming} team${confirming === 1 ? '' : 's'}${decisions.length > confirming ? `, ${decisions.length - confirming} left blank` : ''}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "The award names these teams in its billing table. Confirm them together: edit a name if it is printed differently from how you call the team, or mark one that is not a team."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border/60",
                children: group.members.map((m)=>{
                    const open = isOpenReviewItem(m);
                    const { quote, page } = source(m.review);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5 py-2.5",
                        "data-testid": Testids.reviewItem.groupMember,
                        "data-item-id": m.id,
                        "data-resolution": m.resolutionState,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    open ? /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": `Team name (${statedTeamName(m.review)})`,
                                        value: names[m.id] ?? '',
                                        disabled: !!rejected[m.id] || isViewOnly,
                                        onChange: (e)=>setNames((prev)=>({
                                                    ...prev,
                                                    [m.id]: e.target.value
                                                })),
                                        "data-testid": Testids.reviewItem.groupMemberName,
                                        className: "max-w-xs"
                                    }) : /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: statedTeamName(m.review)
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex-1"
                                    }),
                                    open ? /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                        label: "Not a team",
                                        "data-testid": Testids.reviewItem.groupMemberReject,
                                        controlProps: {
                                            checked: !!rejected[m.id],
                                            disabled: isViewOnly,
                                            onCheckedChange: (v)=>setRejected((prev)=>({
                                                        ...prev,
                                                        [m.id]: v === true
                                                    }))
                                        }
                                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "success",
                                        children: resolutionLabel(m.resolutionState, m.review)
                                    })
                                ]
                            }),
                            quote && /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    "“",
                                    quote.length > 220 ? `${quote.slice(0, 219)}…` : quote,
                                    "”",
                                    page > 0 ? ` · p. ${page}` : ''
                                ]
                            })
                        ]
                    }, m.id);
                })
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    disabled: isViewOnly || resolve.isPending || blankName,
                    onClick: ()=>void onConfirm(),
                    "data-testid": Testids.reviewItem.groupConfirm,
                    children: resolve.isPending ? 'Saving…' : `Confirm ${confirming} team${confirming === 1 ? '' : 's'}${decisions.length > confirming ? ` (${decisions.length - confirming} not a team)` : ''}`
                })
            })
        ]
    });
}
function SameValueGroup({ group, changeSetId, onDone, names }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const first = group.members[0].review;
    const field = first.targetFieldPath;
    const [choice, setChoice] = useState('');
    const [blanking, setBlanking] = useState(false);
    const [reason, setReason] = useState('');
    const openMembers = group.members.filter(isOpenReviewItem);
    const { quote, page } = source(first);
    const lines = group.members.map((m)=>targetName(m.review, names) ? memberLine(m.review, names) : recordLabel(m.review.targetObjectType, m.review.targetLocator));
    const onApply = async ()=>{
        const decisions = [];
        for (const m of openMembers){
            const c = m.review.candidates.find((x)=>x.key === choice);
            if (!c) continue;
            decisions.push({
                discrepancyId: m.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(c, '', field),
                decidedValueJson: c.valueJson
            });
        }
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Set on ${decisions.length} ${memberNoun(group, decisions.length)}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    const onBlank = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions: openMembers.map((m)=>({
                        discrepancyId: m.id,
                        resolutionState: 'Waived',
                        decisionRationale: leftBlankRationale(reason.trim(), m.review)
                    })),
                summary: `Left blank on ${openMembers.length} ${memberNoun(group, openMembers.length)}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    const ruleConflict = isScheduleRuleConflict(first);
    const deliverable = targetName(first, names) || recordLabel(first.targetObjectType, first.targetLocator);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: first.reason
            }),
            quote && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    "“",
                    quote.length > 300 ? `${quote.slice(0, 299)}…` : quote,
                    "”",
                    page > 0 ? ` · p. ${page}` : ''
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: `Applies to ${group.members.length} ${memberNoun(group)}`
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        className: "mt-1",
                        children: lines.join(', ')
                    })
                ]
            }),
            ruleConflict && /*#__PURE__*/ _jsx(ScheduleRuleCards, {
                review: first,
                deliverable: deliverable
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Your call"
                    }),
                    first.candidates.map((c)=>/*#__PURE__*/ _jsx(SpecraButton, {
                            variant: choice === c.key ? 'default' : 'secondary',
                            className: "w-full justify-start",
                            disabled: isViewOnly,
                            onClick: ()=>setChoice(c.key),
                            "data-testid": Testids.reviewItem.groupChoice,
                            "data-key": c.key,
                            "aria-pressed": choice === c.key,
                            children: ruleConflict ? scheduleRuleWords(c) : displayValue(c.valueJson, field) !== c.label && c.label ? c.label : displayValue(c.valueJson, field)
                        }, c.key)),
                    blanking && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Why leave these blank?",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: reason,
                            onChange: (e)=>setReason(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            blanking ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "destructive",
                                size: "sm",
                                disabled: !reason.trim() || isViewOnly || resolve.isPending,
                                onClick: ()=>void onBlank(),
                                "data-testid": Testids.reviewItem.groupLeaveBlank,
                                children: [
                                    "Leave ",
                                    openMembers.length,
                                    " blank"
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "destructive",
                                size: "sm",
                                disabled: isViewOnly,
                                onClick: ()=>setBlanking(true),
                                "data-testid": Testids.reviewItem.groupLeaveBlank,
                                children: "Leave them blank…"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-1"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !choice || isViewOnly || resolve.isPending,
                                onClick: ()=>void onApply(),
                                "data-testid": Testids.reviewItem.groupConfirm,
                                children: resolve.isPending ? 'Saving…' : `Set on ${openMembers.length} ${memberNoun(group, openMembers.length)}`
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
// A row member's choice as the reviewer reads it.
function rowChoiceLabel(c, field) {
    return (c.label ?? '').trim() || displayValue(c.valueJson, field);
}
function RowGroup({ group, changeSetId, onDone }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const openMembers = group.members.filter(isOpenReviewItem);
    // No question starts answered: each is the reviewer's own pick.
    const [answers, setAnswers] = useState({});
    const first = group.members[0].review;
    const page = firstPage(first.sources);
    const decisions = useMemo(()=>{
        const out = [];
        for (const m of openMembers){
            const c = m.review.candidates.find((x)=>x.key === answers[m.id]);
            if (!c) continue;
            out.push({
                discrepancyId: m.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(c, '', m.review.targetFieldPath),
                decidedValueJson: c.valueJson
            });
        }
        return out;
    }, [
        openMembers,
        answers
    ]);
    const onConfirm = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Answered ${decisions.length} of ${openMembers.length} ${memberNoun(group, openMembers.length)} on one SOW row`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    "“",
                    rowPassage(first),
                    "”",
                    page > 0 ? ` · p. ${page}` : ''
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border/60",
                children: group.members.map((m)=>{
                    const open = isOpenReviewItem(m);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5 py-2.5",
                        "data-testid": Testids.reviewItem.groupMember,
                        "data-item-id": m.id,
                        "data-resolution": m.resolutionState,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "font-medium",
                                children: m.review.reason
                            }),
                            open ? /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": `Answer: ${m.review.reason}`,
                                value: answers[m.id] ?? '',
                                disabled: isViewOnly,
                                onChange: (e)=>setAnswers((prev)=>({
                                            ...prev,
                                            [m.id]: e.target.value
                                        })),
                                "data-testid": Testids.reviewItem.groupMemberChoice,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Choose…"
                                    }),
                                    m.review.candidates.map((c)=>/*#__PURE__*/ _jsx("option", {
                                            value: c.key,
                                            children: rowChoiceLabel(c, m.review.targetFieldPath)
                                        }, c.key))
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "success",
                                children: resolutionLabel(m.resolutionState, m.review)
                            })
                        ]
                    }, m.id);
                })
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    disabled: isViewOnly || resolve.isPending || decisions.length === 0,
                    onClick: ()=>void onConfirm(),
                    "data-testid": Testids.reviewItem.groupConfirm,
                    children: resolve.isPending ? 'Saving…' : `Answer ${decisions.length} of ${openMembers.length}`
                })
            })
        ]
    });
}
function RepeatGroup({ group, changeSetId, onDone, onOpenItem }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const [choice, setChoice] = useState('');
    const first = group.members[0].review;
    const field = first.targetFieldPath;
    const openMembers = group.members.filter(isOpenReviewItem);
    const choices = sharedChoices(group.members);
    const docs = groupDocuments(group);
    const openDocs = new Set(openMembers.map((m)=>sowDocumentOf(m.review))).size;
    const onApply = async ()=>{
        const decisions = [];
        for (const m of openMembers){
            const c = m.review.candidates.find((x)=>x.key === choice);
            if (!c) continue;
            decisions.push({
                discrepancyId: m.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(c, '', m.review.targetFieldPath),
                decidedValueJson: c.valueJson
            });
        }
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Answered once for ${docs.length} SOWs (${decisions.length} items)`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: first.reason
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    "“",
                    rowPassage(first),
                    "”"
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: `Applies to ${docs.length} ${memberNoun(group, docs.length)}`
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        className: "mt-1",
                        children: "Each SOW keeps its own record and team. A SOW that routes this elsewhere is answered on its own."
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-1 divide-y divide-border/60",
                        children: group.members.map((m)=>{
                            const page = firstPage(m.review.sources);
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 py-1.5",
                                "data-testid": Testids.reviewItem.groupMember,
                                "data-item-id": m.id,
                                "data-resolution": m.resolutionState,
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "span",
                                        size: "meta",
                                        children: [
                                            sowDocumentOf(m.review),
                                            page > 0 ? ` · p. ${page}` : ''
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex-1"
                                    }),
                                    isOpenReviewItem(m) ? onOpenItem && /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: ()=>onOpenItem(m.id),
                                        children: "Answer on its own"
                                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "success",
                                        children: resolutionLabel(m.resolutionState, m.review)
                                    })
                                ]
                            }, m.id);
                        })
                    })
                ]
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Your call"
                    }),
                    choices.map((c)=>/*#__PURE__*/ _jsx(SpecraButton, {
                            variant: choice === c.key ? 'default' : 'secondary',
                            className: "w-full justify-start",
                            disabled: isViewOnly,
                            onClick: ()=>setChoice(c.key),
                            "data-testid": Testids.reviewItem.groupChoice,
                            "data-key": c.key,
                            "aria-pressed": choice === c.key,
                            children: rowChoiceLabel(c, field)
                        }, c.key)),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            disabled: !choice || isViewOnly || resolve.isPending,
                            onClick: ()=>void onApply(),
                            "data-testid": Testids.reviewItem.groupConfirm,
                            children: resolve.isPending ? 'Saving…' : `Answer for ${openDocs} ${memberNoun(group, openDocs)}`
                        })
                    })
                ]
            })
        ]
    });
}
// The candidate keys of a SOW-silent item (pangaea contract_model_sow_silent.go).
const SILENT_OWED = 'owed';
const SILENT_NOT_OWED = 'not_owed';
const SILENT_NOT_APPLICABLE = 'not_applicable';
// The answer a silent member starts on: the pre-filled one, else owed.
function silentDefault(r) {
    const top = r.candidates.filter((c)=>c.score >= 0.9);
    return top.length === 1 ? top[0].key : SILENT_OWED;
}
// The answer the SOW itself pre-filled: not applicable from its PWS scope
// list (SAD-969), or not owed from its statement that it requires nothing
// beyond the deliverables it lists (SAD-973).
function prefilledBy(r) {
    return r.candidates.find((c)=>(c.key === SILENT_NOT_APPLICABLE || c.key === SILENT_NOT_OWED) && c.score >= 0.9);
}
function prefillBadge(c) {
    if (c.key === SILENT_NOT_APPLICABLE) return 'Pre-filled: outside the SOW’s PWS scope';
    const page = c.sources[0]?.page ?? 0;
    return page > 0 ? `Pre-filled: not owed · SOW p. ${page}` : 'Pre-filled: not owed per the SOW';
}
function SilentGroup({ group, changeSetId, onDone, names }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const openMembers = group.members.filter(isOpenReviewItem);
    const [answers, setAnswers] = useState(()=>Object.fromEntries(group.members.map((m)=>[
                m.id,
                silentDefault(m.review)
            ])));
    const decisions = useMemo(()=>{
        const out = [];
        for (const m of openMembers){
            const c = m.review.candidates.find((x)=>x.key === answers[m.id]);
            if (!c) continue;
            out.push({
                discrepancyId: m.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(c, '', m.review.targetFieldPath),
                decidedValueJson: c.valueJson
            });
        }
        return out;
    }, [
        openMembers,
        answers
    ]);
    const owed = openMembers.filter((m)=>answers[m.id] === SILENT_OWED).length;
    const exceptions = decisions.length - owed;
    const onConfirm = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `${owed} owed per the award schedule${exceptions ? `, ${exceptions} not owed` : ''}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "This SOW never mentions these award deliverables of its team's tasks. Until you answer, each stays owed per the award schedule. Mark any this team does not owe."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border/60",
                children: group.members.map((m)=>{
                    const open = isOpenReviewItem(m);
                    const prefill = prefilledBy(m.review);
                    const scope = prefill?.sources[0];
                    const name = targetName(m.review, names) || memberLine(m.review, names);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5 py-2.5",
                        "data-testid": Testids.reviewItem.groupMember,
                        "data-item-id": m.id,
                        "data-resolution": m.resolutionState,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: name
                                    }),
                                    prefill ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "info",
                                        "data-testid": Testids.reviewItem.groupMemberPrefilled,
                                        children: prefillBadge(prefill)
                                    }) : null,
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex-1"
                                    }),
                                    open ? /*#__PURE__*/ _jsx(SpecraSelect, {
                                        "aria-label": `Answer for ${name}`,
                                        value: answers[m.id] ?? SILENT_OWED,
                                        disabled: isViewOnly,
                                        onChange: (e)=>setAnswers((prev)=>({
                                                    ...prev,
                                                    [m.id]: e.target.value
                                                })),
                                        "data-testid": Testids.reviewItem.groupMemberChoice,
                                        children: m.review.candidates.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                value: c.key,
                                                children: c.label
                                            }, c.key))
                                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "success",
                                        children: resolutionLabel(m.resolutionState, m.review)
                                    })
                                ]
                            }),
                            prefill && scope?.quote ? /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    "“",
                                    scope.quote.length > 220 ? `${scope.quote.slice(0, 219)}…` : scope.quote,
                                    "”",
                                    scope.page > 0 ? ` · p. ${scope.page}` : ''
                                ]
                            }) : null
                        ]
                    }, m.id);
                })
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    disabled: isViewOnly || resolve.isPending || decisions.length === 0,
                    onClick: ()=>void onConfirm(),
                    "data-testid": Testids.reviewItem.groupConfirm,
                    children: resolve.isPending ? 'Saving…' : `Answer ${decisions.length}: ${owed} owed${exceptions ? `, ${exceptions} not owed` : ''}`
                })
            })
        ]
    });
}
